import React, { useState, useEffect } from 'react';
import './Scrapers.css';

function Scrapers() {
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [postEventRunning, setPostEventRunning] = useState(false);
  const [postEventResult, setPostEventResult] = useState(null);
  const postEventWasRunning = React.useRef(false);

  const [calibrationRunning, setCalibrationRunning] = useState(false);
  const [calibrationResult, setCalibrationResult] = useState(null);
  const [calStartDate, setCalStartDate] = useState('2018-01-01');
  const [calEndDate, setCalEndDate] = useState('2025-12-31');

  const [adjustmentsRunning, setAdjustmentsRunning] = useState(false);
  const [adjustmentsResult, setAdjustmentsResult] = useState(null);

  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  const [trainRunning, setTrainRunning] = useState(false);
  const [trainResult, setTrainResult] = useState(null);
  const [trainMetrics, setTrainMetrics] = useState(null);

  const [gbmRunning, setGbmRunning] = useState(false);
  const [gbmResult, setGbmResult] = useState(null);
  const [gbmMetrics, setGbmMetrics] = useState(null);

  const [closingRunning, setClosingRunning] = useState(false);
  const [closingResult, setClosingResult] = useState(null);

  const [glicko2Running, setGlicko2Running] = useState(false);
  const [glicko2Result, setGlicko2Result] = useState(null);

  const [methodRunning, setMethodRunning] = useState(false);
  const [methodResult, setMethodResult] = useState(null);
  const [methodMetrics, setMethodMetrics] = useState(null);

  const [diagOpen, setDiagOpen] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const [wfRunning, setWfRunning] = useState(false);
  const [wfResult, setWfResult] = useState(null);
  const [wfData, setWfData] = useState(null);

  const [modelStatus, setModelStatus] = useState(null);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchModelStatus = async () => {
      try {
        const res = await fetch('/api/model-status', { credentials: 'include' });
        const data = await res.json();
        setModelStatus(data);
      } catch (e) { /* ignore */ }
    };
    fetchModelStatus();
    const iv = setInterval(fetchModelStatus, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (diagOpen && !diagData) fetchDiagnostics();
  }, [diagOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load walk-forward results (and poll while running)
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/model/walkforward', { credentials: 'include' });
        const data = await res.json();
        setWfRunning(data.running || false);
        if (data.results) setWfData(data.results);
        if (data.running) setWfResult({ ok: null, text: data.message || 'Running…' });
        else if (data.error) setWfResult({ ok: false, text: data.error });
        else if (data.results) setWfResult({ ok: true, text: data.message || 'Complete' });
      } catch (e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRelativeTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/scraper/status', { credentials: 'include' });
      const data = await response.json();
      setStatus(data);
      // Detect post-event completion via ref (avoids stale closure over postEventRunning state)
      const nowRunning = !!data.post_event?.running;
      if (nowRunning) {
        postEventWasRunning.current = true;
      } else if (postEventWasRunning.current) {
        postEventWasRunning.current = false;
        setPostEventRunning(false);
        const hasErrors = (data.post_event?.step_logs || []).some(l => l.status === 'error');
        if (data.post_event?.last_success_result) {
          setPostEventResult({ ok: !hasErrors, text: data.post_event.last_success_result });
        }
      }
    } catch (error) {
      console.error('Error fetching scraper status:', error);
    }
  };

  const runScraper = async (scraperType) => {
    try {
      const response = await fetch(`/api/scraper/run/${scraperType}`, { method: 'POST', credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setMessage(`✓ ${data.message}`);
        setTimeout(() => setMessage(''), 4000);
      } else {
        setMessage(`✗ ${data.error}`);
      }
    } catch (error) {
      setMessage(`✗ Error: ${error.message}`);
    }
  };

  const runCalibration = async () => {
    setCalibrationRunning(true);
    setCalibrationResult(null);
    try {
      const res = await fetch('/api/calibrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ start_date: calStartDate, end_date: calEndDate })
      });
      const data = await res.json();
      if (data.success) {
        const text = data.method === 'platt'
          ? `Platt scaling: A=${data.A.toFixed(3)}, B=${data.B.toFixed(3)}`
          : `Built ${data.calibration_points} calibration points`;
        setCalibrationResult({ ok: true, text });
      } else {
        setCalibrationResult({ ok: false, text: data.error || 'Calibration failed' });
      }
    } catch (e) {
      setCalibrationResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setCalibrationRunning(false);
    }
  };

  const runRebuildRatings = async () => {
    setRebuildRunning(true);
    setRebuildResult(null);
    try {
      const res = await fetch('/api/model/rebuild-ratings', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setRebuildResult({ ok: true, text: `Inserted ${data.rows_inserted.toLocaleString()} historical snapshots` });
      } else {
        setRebuildResult({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setRebuildResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setRebuildRunning(false);
    }
  };

  const runTrainModel = async () => {
    setTrainRunning(true);
    setTrainResult(null);
    setTrainMetrics(null);
    try {
      const res = await fetch('/api/model/train', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const m = data.metrics;
        setTrainMetrics(m);
        setTrainResult({
          ok: true,
          text: `Train ${(m.train?.accuracy * 100).toFixed(1)}%${m.test ? `  |  Test ${(m.test.accuracy * 100).toFixed(1)}%  |  Brier ${m.test.brier_score}` : '  |  No test split (train on all data)'}`
        });
      } else {
        setTrainResult({ ok: false, text: data.error || 'Training failed' });
      }
    } catch (e) {
      setTrainResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setTrainRunning(false);
    }
  };

  const runTrainGbm = async () => {
    setGbmRunning(true);
    setGbmResult(null);
    setGbmMetrics(null);
    try {
      const res = await fetch('/api/model/train-gbm', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const m = data.metrics;
        setGbmMetrics(m);
        setGbmResult({
          ok: true,
          text: `Train ${(m.train?.accuracy * 100).toFixed(1)}%${m.test ? `  |  Test ${(m.test.accuracy * 100).toFixed(1)}%` : '  |  No test split'}  |  ${m.n_features} features`
        });
      } else {
        setGbmResult({ ok: false, text: data.error || 'GBM training failed' });
      }
    } catch (e) {
      setGbmResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setGbmRunning(false);
    }
  };

  const runWalkForward = async () => {
    setWfRunning(true);
    setWfResult({ ok: null, text: 'Starting…' });
    setWfData(null);
    try {
      const res = await fetch('/api/model/walkforward', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ window_years: 5 }),
      });
      const data = await res.json();
      if (!data.success) {
        setWfRunning(false);
        setWfResult({ ok: false, text: data.error || 'Failed to start' });
      }
      // results arrive via polling
    } catch (e) {
      setWfRunning(false);
      setWfResult({ ok: false, text: `Error: ${e.message}` });
    }
  };

  const runClosingLines = async () => {
    setClosingRunning(true);
    setClosingResult(null);
    try {
      const res = await fetch('/api/scraper/run/closing-lines', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setClosingResult({ ok: true, text: `Captured ${data.lines_captured} closing lines` });
      } else {
        setClosingResult({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setClosingResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setClosingRunning(false);
    }
  };

  const runGlicko2 = async () => {
    setGlicko2Running(true);
    setGlicko2Result(null);
    try {
      const res = await fetch('/api/model/calculate-glicko2', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setGlicko2Result({ ok: true, text: `Rated ${data.fighters_rated} fighters from ${data.fights_processed} fights` });
      } else {
        setGlicko2Result({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setGlicko2Result({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setGlicko2Running(false);
    }
  };

  const runTrainMethod = async () => {
    setMethodRunning(true);
    setMethodResult(null);
    setMethodMetrics(null);
    try {
      const res = await fetch('/api/model/train-method', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const acc = data.metrics?.test?.accuracy;
        setMethodResult({ ok: true, text: `Test accuracy: ${(acc * 100).toFixed(1)}%` });
        setMethodMetrics(data.metrics);
      } else {
        setMethodResult({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setMethodResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setMethodRunning(false);
    }
  };

  const runPostEvent = async () => {
    setPostEventRunning(true);
    setPostEventResult(null);
    try {
      const res = await fetch('/api/scraper/run/post-event', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!data.success) setPostEventResult({ ok: false, text: data.error || 'Failed to start' });
    } catch (e) {
      setPostEventResult({ ok: false, text: `Error: ${e.message}` });
      setPostEventRunning(false);
    }
  };

  const runAdjustments = async () => {
    setAdjustmentsRunning(true);
    setAdjustmentsResult(null);
    try {
      const res = await fetch('/api/apply-adjustments', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setAdjustmentsResult({ ok: true, text: data.message });
      } else {
        setAdjustmentsResult({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setAdjustmentsResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setAdjustmentsRunning(false);
    }
  };

  const fetchDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const res = await fetch('/api/debug/db-info', { credentials: 'include' });
      const data = await res.json();
      setDiagData(data);
    } catch (e) {
      setDiagData({ error: e.message });
    } finally {
      setDiagLoading(false);
    }
  };

  const runCleanup = async () => {
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const res = await fetch('/api/admin/cleanup-events', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const parts = [];
        if (data.past_events_fixed > 0) parts.push(`${data.past_events_fixed} past events marked completed`);
        if (data.null_events_deleted > 0) parts.push(`${data.null_events_deleted} garbage events deleted (${data.null_fights_deleted} fights)`);
        if (data.duplicate_events_merged > 0) parts.push(`${data.duplicate_events_merged} duplicate date(s) merged (${data.fights_reassigned || 0} fights reassigned)`);
        if (data.wrong_date_events_merged > 0) parts.push(`${data.wrong_date_events_merged} wrong-date event(s) removed (${data.wrong_date_fights_reassigned || 0} duplicate fights)`);
        setCleanupResult({ ok: true, text: parts.length ? parts.join(', ') : 'Nothing to clean up' });
        fetchDiagnostics();
      } else {
        setCleanupResult({ ok: false, text: data.error || 'Failed' });
      }
    } catch (e) {
      setCleanupResult({ ok: false, text: `Error: ${e.message}` });
    } finally {
      setCleanupRunning(false);
    }
  };

  if (!status) {
    return <div className="loading">Loading scrapers...</div>;
  }

  const LastSuccess = ({ opKey, scraper }) => {
    const s = scraper || status?.[opKey];
    const ts = s?.last_success;
    const result = s?.last_success_result;
    if (!ts) return <span className="status-never">Never run</span>;
    return (
      <span className="status-success-time">
        {formatRelativeTime(ts)}
        {result && <span className="status-success-result"> — {result}</span>}
      </span>
    );
  };

  const isAnyRunning = Object.values(status).some(s => s?.running);
  // Derive running state from backend (survives page refresh)
  const isPostEventRunning = postEventRunning || !!status?.post_event?.running;
  const stepLogs = status?.post_event?.step_logs || [];
  const stepErrors = stepLogs.filter(l => l.status === 'error');

  const ActiveModelBadge = () => {
    if (!modelStatus) return null;
    const { gbm_n_features, gbm_trained_at,
            ml_model_accuracy, ml_trained_at, ml_model_trained } = modelStatus;
    // active_model is only present after the new backend is deployed;
    // fall back to inferring from older fields so the badge is never wrong.
    const active_model = modelStatus.active_model
      || (modelStatus.gbm_trained ? 'gbm' : (ml_model_trained ? 'lr' : 'elo_power'));
    // Prefer walk-forward avg accuracy (honest out-of-sample) over train accuracy
    const wfAvg = wfData?.full_history?.summary?.avg_accuracy;
    if (active_model === 'gbm') {
      return (
        <div className="model-status-bar model-status-gbm">
          <span className="model-status-icon">✓</span>
          <span className="model-status-name">GBM Active</span>
          {gbm_n_features && <span className="model-status-detail">{gbm_n_features} features</span>}
          {wfAvg
            ? <span className="model-status-detail">{(wfAvg * 100).toFixed(1)}% walk-forward acc</span>
            : null}
          {gbm_trained_at && <span className="model-status-date">trained {formatRelativeTime(gbm_trained_at)}</span>}
        </div>
      );
    }
    if (active_model === 'lr') {
      return (
        <div className="model-status-bar model-status-lr">
          <span className="model-status-icon">⚠</span>
          <span className="model-status-name">Logistic Regression</span>
          <span className="model-status-detail">GBM not trained — using LR fallback</span>
          {ml_model_accuracy && <span className="model-status-detail">{(ml_model_accuracy * 100).toFixed(1)}% test acc</span>}
          {ml_trained_at && <span className="model-status-date">trained {formatRelativeTime(ml_trained_at)}</span>}
        </div>
      );
    }
    return (
      <div className="model-status-bar model-status-fallback">
        <span className="model-status-icon">✗</span>
        <span className="model-status-name">Elo + Power Fallback</span>
        <span className="model-status-detail">No ML model trained — predictions are low quality</span>
      </div>
    );
  };

  return (
    <div className="scrapers">
      <h2>Operations</h2>

      {message && (
        <div className={`message ${message.startsWith('✓') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <ActiveModelBadge />

      {/* ── Post-Event Refresh ── */}
      <div className="post-event-banner">
        <div className="post-event-left" style={{ flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
          <div className="post-event-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="post-event-title">Post-Event Refresh</div>
              <div className="post-event-subtitle">
                {isPostEventRunning && status?.post_event?.step
                  ? `Step ${status.post_event.step_index ?? '?'} of ${status.post_event.steps_total ?? 5}: ${status.post_event.step}…`
                  : 'Fighters → Events → Ratings → Glicko-2 → Odds in one click'}
              </div>
              {postEventResult && (
                <div className={`op-result ${postEventResult.ok ? 'op-result-ok' : 'op-result-err'}`} style={{ marginTop: 6 }}>
                  {postEventResult.text}
                </div>
              )}
              {!postEventResult && !isPostEventRunning && status?.post_event?.last_success && (
                <div className="status-success-time" style={{ marginTop: 4 }}>
                  Last run: <LastSuccess opKey="post_event" />
                  {stepErrors.length > 0 && (
                    <span style={{ color: '#f87171', marginLeft: 8 }}>
                      {stepErrors.length} step{stepErrors.length > 1 ? 's' : ''} failed
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              className="btn btn-primary post-event-btn"
              onClick={runPostEvent}
              disabled={isPostEventRunning || isAnyRunning}
            >
              {isPostEventRunning ? 'Running…' : 'Run Post-Event Refresh'}
            </button>
          </div>

          {/* Step logs — shown during run and after (persisted via DB) */}
          {stepLogs.length > 0 && (
            <div className="step-log-list">
              {stepLogs.map((log, i) => (
                <div key={i} className={`step-log-entry step-log-${log.status}`}>
                  <span className="step-log-icon">{log.status === 'ok' ? '✓' : '✗'}</span>
                  <span className="step-log-name">{log.step}</span>
                  {log.duration_s != null && (
                    <span className="step-log-duration">{log.duration_s}s</span>
                  )}
                  <span className="step-log-result">
                    {log.status === 'ok' ? log.result : log.error}
                  </span>
                </div>
              ))}
              {isPostEventRunning && status?.post_event?.step_index > stepLogs.length && (
                <div className="step-log-entry step-log-running">
                  <span className="step-log-icon">⟳</span>
                  <span className="step-log-name">{status.post_event.step}</span>
                  <span className="step-log-result">running…</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Weekly Workflow ── */}
      <div className="workflow-section">
        <div className="section-header">
          <div>
            <h3 className="section-title">Weekly Refresh</h3>
            <p className="section-subtitle">Run these in order before each event to keep predictions current</p>
          </div>
        </div>

        <div className="workflow-grid">

          {/* Step 1: Events */}
          <div className="scraper-card">
            <div className="scraper-header">
              <span className="step-badge">1</span>
              <div className="scraper-header-text">
                <h3>Upcoming Events</h3>
                <span className="when-tag">When: New event announced</span>
              </div>
            </div>
            <p className="scraper-description">Pulls the latest UFC fight cards. Run this first whenever a new event appears.</p>
            <div className="scraper-status">
              {status.events.running && <RunningBar status={status.events} />}
              <div className="status-footer">
                <LastSuccess scraper={status.events} />
                <button
                  className="btn btn-primary btn-inline"
                  onClick={() => runScraper('events')}
                  disabled={status.events.running}
                >
                  {status.events.running ? 'Running…' : 'Run'}
                </button>
              </div>
              {status.events.last_success_result && !status.events.running && (
                <div className="op-result op-result-ok">{status.events.last_success_result}</div>
              )}
            </div>
          </div>

          {/* Step 2: Fighters */}
          <div className="scraper-card">
            <div className="scraper-header">
              <span className="step-badge">2</span>
              <div className="scraper-header-text">
                <h3>Fighter Database</h3>
                <span className="when-tag">When: Weekly · ~15 min</span>
              </div>
            </div>
            <p className="scraper-description">Refreshes fighter stats and fight history. Run once a week or after UFC adds new fighters.</p>
            <div className="scraper-status">
              {status.fighters.running && <RunningBar status={status.fighters} />}
              <div className="status-footer">
                <LastSuccess scraper={status.fighters} />
                <button
                  className="btn btn-primary btn-inline"
                  onClick={() => runScraper('fighters')}
                  disabled={status.fighters.running}
                >
                  {status.fighters.running ? 'Running…' : 'Run'}
                </button>
              </div>
              {status.fighters.last_success_result && !status.fighters.running && (
                <div className="op-result op-result-ok">{status.fighters.last_success_result}</div>
              )}
            </div>
          </div>

          {/* Step 3: Ratings */}
          <div className="scraper-card">
            <div className="scraper-header">
              <span className="step-badge">3</span>
              <div className="scraper-header-text">
                <h3>Power Ratings</h3>
                <span className="when-tag">When: After fighters update</span>
              </div>
            </div>
            <p className="scraper-description">Recalculates power ratings and Elo for all fighters, then auto-applies fight adjustments (layoff, matchup, weight cut).</p>
            <div className="scraper-status">
              {status.ratings.running && <RunningBar status={status.ratings} />}
              <div className="status-footer">
                <LastSuccess scraper={status.ratings} />
                <button
                  className="btn btn-primary btn-inline"
                  onClick={() => runScraper('ratings')}
                  disabled={status.ratings.running}
                >
                  {status.ratings.running ? 'Running…' : 'Run'}
                </button>
              </div>
              {status.ratings.last_success_result && !status.ratings.running && (
                <div className="op-result op-result-ok">{status.ratings.last_success_result}</div>
              )}
            </div>
          </div>

          {/* Step 4: Odds */}
          <div className="scraper-card">
            <div className="scraper-header">
              <span className="step-badge">4</span>
              <div className="scraper-header-text">
                <h3>Bookmaker Odds</h3>
                <span className="when-tag">When: Anytime, before betting</span>
              </div>
            </div>
            <p className="scraper-description">Fetches the latest lines from all bookmakers. Run this right before you review bets.</p>
            <div className="scraper-status">
              {status.odds.running && <RunningBar status={status.odds} />}
              <div className="status-footer">
                <LastSuccess scraper={status.odds} />
                <button
                  className="btn btn-primary btn-inline"
                  onClick={() => runScraper('odds')}
                  disabled={status.odds.running}
                >
                  {status.odds.running ? 'Running…' : 'Run'}
                </button>
              </div>
              {status.odds.last_success_result && !status.odds.running && (
                <div className="op-result op-result-ok">{status.odds.last_success_result}</div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── Model Management (collapsible) ── */}
      <div className="advanced-section">
        <button className="advanced-toggle" onClick={() => setAdvancedOpen(o => !o)}>
          <span className="advanced-toggle-title">Model Management</span>
          <span className="advanced-toggle-hint">Retrain, calibrate, fix adjustments — rarely needed</span>
          <span className="advanced-chevron">{advancedOpen ? '▲' : '▼'}</span>
        </button>

        {advancedOpen && (
          <div className="advanced-grid">

            {/* Rebuild */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Rebuild Historical Ratings</h3>
                  <span className="when-tag when-tag-dim">Step 1 of 2 before retraining</span>
                </div>
              </div>
              <p className="scraper-description">
                Recomputes bias-free historical power rating snapshots from scratch. Required before training the ML model. Takes 2–3 min.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="rebuild" />
                  <button className="btn btn-secondary btn-inline" onClick={runRebuildRatings} disabled={rebuildRunning}>
                    {rebuildRunning ? 'Rebuilding…' : 'Rebuild'}
                  </button>
                </div>
                {rebuildResult && (
                  <div className={`op-result ${rebuildResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {rebuildResult.text}
                  </div>
                )}
              </div>
            </div>

            {/* Train */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Train ML Model</h3>
                  <span className="when-tag when-tag-dim">Step 2 of 2 · run after Rebuild · ~5 min</span>
                </div>
              </div>
              <p className="scraper-description">
                Trains logistic regression on 10 years of fight data. Replaces the active model immediately on completion.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="train" />
                  <button className="btn btn-secondary btn-inline" onClick={runTrainModel} disabled={trainRunning}>
                    {trainRunning ? 'Training…' : 'Train'}
                  </button>
                </div>
                {trainResult && (
                  <div className={`op-result ${trainResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {trainResult.text}
                  </div>
                )}
                {trainMetrics && (
                  <div className="op-result op-result-ok" style={{ marginTop: 0 }}>
                    n={trainMetrics.train.n.toLocaleString()} train · n={trainMetrics.test.n.toLocaleString()} test
                    &nbsp;·&nbsp; {trainMetrics.train_period} → {trainMetrics.test_period}
                  </div>
                )}
              </div>
            </div>

            {/* Train GBM */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Train GBM Model</h3>
                  <span className="when-tag when-tag-dim">After Rebuild · trains on all data · ~5 min</span>
                </div>
              </div>
              <p className="scraper-description">
                Trains LightGBM on all historical fights (2015→today) using 41 symmetric features. No test split — use Walk-Forward Validation for honest accuracy estimates.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="train_gbm" />
                  <button className="btn btn-secondary btn-inline" onClick={runTrainGbm} disabled={gbmRunning}>
                    {gbmRunning ? 'Training…' : 'Train GBM'}
                  </button>
                </div>
                {gbmResult && (
                  <div className={`op-result ${gbmResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {gbmResult.text}
                  </div>
                )}
                {gbmMetrics && (
                  <div className="op-result op-result-ok" style={{ marginTop: 0 }}>
                    n={gbmMetrics.train?.n?.toLocaleString()} examples · {gbmMetrics.train_period}
                  </div>
                )}
              </div>
            </div>

            {/* Walk-Forward Validation */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Walk-Forward Validation</h3>
                  <span className="when-tag when-tag-dim">After GBM train · ~20 min</span>
                </div>
              </div>
              <p className="scraper-description">
                Honest accuracy test: trains on past data, tests on each year 2022–2025 sequentially.
                Compares <strong>full history</strong> vs <strong>5-year rolling window</strong> to detect concept drift.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <span className="last-success-label">
                    {wfData ? `Last run: ${wfData.completed_at?.split('T')[0]}` : ''}
                  </span>
                  <button className="btn btn-secondary btn-inline" onClick={runWalkForward} disabled={wfRunning}>
                    {wfRunning ? 'Running…' : 'Run Validation'}
                  </button>
                </div>
                {wfResult && (
                  <div className={`op-result ${wfResult.ok === true ? 'op-result-ok' : wfResult.ok === false ? 'op-result-err' : 'op-result-running'}`}>
                    {wfResult.text}
                  </div>
                )}
                {wfData && !wfRunning && (
                  <div className="wf-results">
                    <table className="wf-table">
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Full History</th>
                          <th>5yr Window</th>
                          <th>Winner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(wfData.full_history?.folds || []).map((fold, i) => {
                          const rolling = (wfData.rolling_window?.folds || [])[i];
                          if (fold.error) return (
                            <tr key={fold.test_year}>
                              <td>{fold.test_year}</td>
                              <td colSpan={3} className="wf-error">{fold.error}</td>
                            </tr>
                          );
                          const fullAcc = fold.accuracy;
                          const rollAcc = rolling && !rolling.error ? rolling.accuracy : null;
                          const fullWins = rollAcc === null || fullAcc >= rollAcc;
                          return (
                            <tr key={fold.test_year}>
                              <td>{fold.test_year}</td>
                              <td className={fullWins ? 'wf-winner' : ''}>{(fullAcc * 100).toFixed(1)}%</td>
                              <td className={!fullWins ? 'wf-winner' : ''}>{rollAcc !== null ? `${(rollAcc * 100).toFixed(1)}%` : '—'}</td>
                              <td className="wf-winner-label">{fullWins ? 'Full' : `${wfData.window_years}yr`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="wf-avg-row">
                          <td>Avg</td>
                          <td className={
                            (wfData.full_history?.summary?.avg_accuracy || 0) >= (wfData.rolling_window?.summary?.avg_accuracy || 0)
                              ? 'wf-winner' : ''
                          }>
                            {wfData.full_history?.summary?.avg_accuracy
                              ? `${(wfData.full_history.summary.avg_accuracy * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td className={
                            (wfData.rolling_window?.summary?.avg_accuracy || 0) > (wfData.full_history?.summary?.avg_accuracy || 0)
                              ? 'wf-winner' : ''
                          }>
                            {wfData.rolling_window?.summary?.avg_accuracy
                              ? `${(wfData.rolling_window.summary.avg_accuracy * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td className="wf-winner-label">
                            {wfData.full_history?.summary?.avg_accuracy && wfData.rolling_window?.summary?.avg_accuracy
                              ? ((wfData.full_history.summary.avg_accuracy >= wfData.rolling_window.summary.avg_accuracy)
                                  ? '✓ Full wins' : `✓ ${wfData.window_years}yr wins`)
                              : ''}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Calculate Glicko-2 */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Calculate Glicko-2</h3>
                  <span className="when-tag when-tag-dim">After fighters update</span>
                </div>
              </div>
              <p className="scraper-description">
                Calculates Glicko-2 ratings with Rating Deviation (uncertainty). RD increases during inactivity, providing better confidence scaling for Kelly sizing.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="glicko2" />
                  <button className="btn btn-secondary btn-inline" onClick={runGlicko2} disabled={glicko2Running}>
                    {glicko2Running ? 'Calculating...' : 'Calculate'}
                  </button>
                </div>
                {glicko2Result && (
                  <div className={`op-result ${glicko2Result.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {glicko2Result.text}
                  </div>
                )}
              </div>
            </div>

            {/* Train Method Model */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Train Method Model</h3>
                  <span className="when-tag when-tag-dim">After GBM train · KO/Sub/Dec</span>
                </div>
              </div>
              <p className="scraper-description">
                Trains a multi-class LightGBM to predict Method of Victory: KO/TKO, Submission, or Decision. Uses the same enhanced features as the win/loss GBM.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="train_method" />
                  <button className="btn btn-secondary btn-inline" onClick={runTrainMethod} disabled={methodRunning}>
                    {methodRunning ? 'Training...' : 'Train Method'}
                  </button>
                </div>
                {methodResult && (
                  <div className={`op-result ${methodResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {methodResult.text}
                  </div>
                )}
                {methodMetrics && methodMetrics.test && methodMetrics.test.per_class && (
                  <div className="op-result op-result-ok" style={{ marginTop: 0 }}>
                    {Object.entries(methodMetrics.test.per_class).map(([cls, m]) =>
                      `${cls}: ${(m.accuracy * 100).toFixed(0)}% (n=${m.count})`
                    ).join(' · ')}
                  </div>
                )}
              </div>
            </div>

            {/* Calibration */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Probability Calibration</h3>
                  <span className="when-tag when-tag-dim">Periodic — not after every event</span>
                </div>
              </div>
              <p className="scraper-description">
                Corrects systematic over/under-confidence in the Elo fallback model. Only affects the fallback path; ML model is already calibrated.
              </p>
              <div className="scraper-status">
                <div className="cal-date-row">
                  <div className="cal-date-field">
                    <label>From</label>
                    <input type="date" value={calStartDate} onChange={e => setCalStartDate(e.target.value)} />
                  </div>
                  <div className="cal-date-field">
                    <label>To</label>
                    <input type="date" value={calEndDate} onChange={e => setCalEndDate(e.target.value)} />
                  </div>
                </div>
                <div className="status-footer">
                  <LastSuccess opKey="calibration" />
                  <button className="btn btn-secondary btn-inline" onClick={runCalibration} disabled={calibrationRunning}>
                    {calibrationRunning ? 'Calibrating…' : 'Calibrate'}
                  </button>
                </div>
                {calibrationResult && (
                  <div className={`op-result ${calibrationResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {calibrationResult.text}
                  </div>
                )}
              </div>
            </div>

            {/* Apply Adjustments */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Apply Adjustments</h3>
                  <span className="when-tag when-tag-dim">Fallback only — auto-runs after Power Ratings</span>
                </div>
              </div>
              <p className="scraper-description">
                Re-applies layoff, style matchup, weight cut, and short-notice penalties. Only use this if you scraped a new event without running Power Ratings afterward.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="adjustments" />
                  <button className="btn btn-secondary btn-inline" onClick={runAdjustments} disabled={adjustmentsRunning}>
                    {adjustmentsRunning ? 'Applying…' : 'Apply'}
                  </button>
                </div>
                {adjustmentsResult && (
                  <div className={`op-result ${adjustmentsResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {adjustmentsResult.text}
                  </div>
                )}
              </div>
            </div>

            {/* Capture Closing Lines */}
            <div className="scraper-card scraper-card-advanced">
              <div className="scraper-header">
                <div className="scraper-header-text">
                  <h3>Capture Closing Lines</h3>
                  <span className="when-tag when-tag-dim">30-60 min before event starts</span>
                </div>
              </div>
              <p className="scraper-description">
                Snapshots the current odds as closing lines for all upcoming fights. Used to calculate CLV (Closing Line Value) — the #1 predictor of long-term profitability.
              </p>
              <div className="scraper-status">
                <div className="status-footer">
                  <LastSuccess opKey="closing_lines" />
                  <button className="btn btn-secondary btn-inline" onClick={runClosingLines} disabled={closingRunning}>
                    {closingRunning ? 'Capturing…' : 'Capture'}
                  </button>
                </div>
                {closingResult && (
                  <div className={`op-result ${closingResult.ok ? 'op-result-ok' : 'op-result-err'}`}>
                    {closingResult.text}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ── Database Health (collapsible) ── */}
      <div className="advanced-section" style={{ marginTop: '0.5rem' }}>
        <button className="advanced-toggle" onClick={() => setDiagOpen(o => !o)}>
          <span className="advanced-toggle-title">Database Health</span>
          <span className="advanced-toggle-hint">Diagnostics, cleanup, data integrity</span>
          <span className="advanced-chevron">{diagOpen ? '▲' : '▼'}</span>
        </button>

        {diagOpen && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
              <button className="btn btn-secondary btn-inline" onClick={fetchDiagnostics} disabled={diagLoading}>
                {diagLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {diagData && !diagData.error && (
              <div className="diag-grid">
                <DiagRow
                  label="GBM Model"
                  ok={diagData.gbm_model_present}
                  text={diagData.gbm_model_present
                    ? `Trained ${diagData.gbm_trained_at?.split('T')[0]}`
                    : 'Not trained — run Train GBM'}
                />
                <DiagRow
                  label="Glicko-2 RD"
                  ok={diagData.fighters_at_default_rd === 0}
                  text={diagData.fighters_at_default_rd === 0
                    ? `OK — avg RD = ${diagData.avg_rd}`
                    : `${diagData.fighters_at_default_rd}/${diagData.total_fighters} fighters at default RD=350 — run Calculate Glicko-2`}
                />
                <DiagRow
                  label="Past Events"
                  ok={diagData.past_upcoming_events === 0}
                  text={diagData.past_upcoming_events === 0
                    ? 'All past events marked completed'
                    : `${diagData.past_upcoming_events} past event(s) still marked 'upcoming'`}
                />
                <DiagRow
                  label="Garbage Events"
                  ok={diagData.null_date_events === 0}
                  text={diagData.null_date_events === 0
                    ? 'No NULL-date events'
                    : `${diagData.null_date_events} events with no date (${diagData.null_date_fights} fights)`}
                />
                <DiagRow
                  label="LightGBM"
                  ok={diagData.lightgbm_importable}
                  text={diagData.lightgbm_importable ? 'Importable' : `Import error: ${diagData.lightgbm_error}`}
                />
                <DiagRow
                  label="Adjustments"
                  ok={diagData.fight_adjustments_total > 0}
                  text={`${diagData.fight_adjustments_total} total — ${
                    Object.entries(diagData.fight_adjustments_by_type || {})
                      .map(([t, n]) => `${t}:${n}`).join(', ') || 'none'
                  }`}
                />
              </div>
            )}

            {diagData && diagData.error && (
              <div className="op-result op-result-err">{diagData.error}</div>
            )}

            {diagData && !diagData.error && (
              <div style={{ marginTop: '0.75rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={runCleanup}
                  disabled={cleanupRunning}
                  style={{ width: '100%' }}
                >
                  {cleanupRunning
                    ? 'Cleaning up…'
                    : (diagData.past_upcoming_events > 0 || diagData.null_date_events > 0)
                      ? `Clean Up Events (${diagData.past_upcoming_events} past + ${diagData.null_date_events} garbage)`
                      : 'Clean up events'}
                </button>
                {cleanupResult && (
                  <div className={`op-result ${cleanupResult.ok ? 'op-result-ok' : 'op-result-err'}`} style={{ marginTop: '0.5rem' }}>
                    {cleanupResult.text}
                  </div>
                )}
                {!cleanupResult && diagData.past_upcoming_events === 0 && diagData.null_date_events === 0 && (
                  <div className="op-result op-result-ok" style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
                    Run cleanup to merge duplicate events on the same date, mark past events completed, or remove garbage.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagRow({ label, ok, text }) {
  return (
    <div className="diag-row">
      <span className="diag-label">{label}</span>
      <span className="diag-value" style={{ color: ok ? '#34d399' : '#f87171' }}>{text}</span>
    </div>
  );
}

function RunningBar({ status }) {
  return (
    <div className="progress-container">
      <div
        className="progress-bar"
        style={{
          width: status.progress_total > 0
            ? `${(status.progress / status.progress_total * 100).toFixed(0)}%`
            : '100%',
          animation: status.progress_total === 0 ? 'indeterminate 1.5s infinite' : 'none'
        }}
      />
      {status.progress_total > 0 && (
        <span className="progress-text">{status.progress} / {status.progress_total}</span>
      )}
    </div>
  );
}

export default Scrapers;
